import { Users, Flame, BookmarkCheck, Home, Euro, TrendingUp, ArrowRight } from "lucide-react";
import { Card } from "./ui";
import { ESTADOS, ESTADO_COLORS, fmtMoney, fmtDate, day } from "./store";

const Kpi = ({ icon: Icon, value, label, tint, testid }) => (
  <Card className="p-5">
    <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: `${tint}18` }}>
      <Icon className="h-4.5 w-4.5 h-[18px] w-[18px]" style={{ color: tint }} />
    </div>
    <p data-testid={testid} className="mt-3 text-2xl font-bold text-gray-900">{value}</p>
    <p className="mt-0.5 text-xs text-gray-500">{label}</p>
  </Card>
);

export const Dashboard = ({ data, go }) => {
  const { leads, apartments, tasks } = data;
  const vendidos = apartments.filter((a) => a.estado === "Vendido").length;
  const cerrados = leads.filter((l) => l.estado === "Cerrado").length;
  const avanzados = leads.filter((l) => ["Reserva", "Contrato", "Cerrado"].includes(l.estado)).length;

  const kpis = [
    { icon: Users, value: leads.length, label: "Total Leads", tint: "#3b82f6", testid: "kpi-total-leads" },
    { icon: Flame, value: leads.filter((l) => l.interes === "Caliente").length, label: "Leads Calientes", tint: "#ef4444", testid: "kpi-calientes" },
    { icon: BookmarkCheck, value: avanzados, label: "Reservas", tint: "#f59e0b", testid: "kpi-reservas" },
    { icon: Home, value: `${vendidos}/18`, label: "Apartamentos Vendidos", tint: "#2d5a3d", testid: "kpi-vendidos" },
    { icon: Euro, value: fmtMoney(vendidos * 59600), label: "Ingresos Estimados", tint: "#16a34a", testid: "kpi-ingresos" },
    { icon: TrendingUp, value: `${leads.length ? Math.round((cerrados / leads.length) * 100) : 0}%`, label: "Tasa de Conversión", tint: "#8b5cf6", testid: "kpi-conversion" },
  ];

  const counts = ESTADOS.map((e) => ({ e, n: leads.filter((l) => l.estado === e).length }));
  const maxN = Math.max(1, ...counts.map((c) => c.n));

  const actividad = leads
    .flatMap((l) => l.interacciones.map((i) => ({ ...i, lead: l.nombre })))
    .sort((a, b) => b.fecha.localeCompare(a.fecha))
    .slice(0, 5);

  const seguimientosHoy = leads.filter((l) => l.fechaProxima === day(0)).length;
  const leadsSemana = leads.filter((l) => l.creado >= day(-7)).length;
  const pendientes = tasks.filter((t) => t.estado !== "Completada").sort((a, b) => a.fechaLimite.localeCompare(b.fechaLimite)).slice(0, 4);
  const leadName = (id) => leads.find((l) => l.id === id)?.nombre || "";

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        {kpis.map((k) => <Kpi key={k.label} {...k} />)}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card className="p-6 xl:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Embudo de ventas</h3>
              <p className="text-xs text-gray-500">Conversión entre etapas</p>
            </div>
            <button data-testid="dash-ver-pipeline" onClick={() => go("pipeline")} className="inline-flex items-center gap-1 text-xs font-medium text-[#2d5a3d] hover:underline">
              Ver pipeline <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="mt-5 space-y-3">
            {counts.map(({ e, n }) => (
              <div key={e} data-testid={`funnel-${e}`} className="flex items-center gap-3">
                <span className="w-24 shrink-0 text-xs text-gray-600">{e}</span>
                <div className="h-8 flex-1 overflow-hidden rounded-lg bg-gray-50">
                  <div
                    className="flex h-full items-center justify-end rounded-lg px-2 text-xs font-semibold text-white transition-all duration-500"
                    style={{ width: `${Math.max(n ? 12 : 4, (n / maxN) * 100)}%`, backgroundColor: ESTADO_COLORS[e] }}
                  >
                    {n}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-5">
              <p data-testid="dash-leads-semana" className="text-2xl font-bold text-[#2d5a3d]">{leadsSemana}</p>
              <p className="mt-0.5 text-xs text-gray-500">Leads esta semana</p>
            </Card>
            <Card className="border-amber-200 bg-amber-50 p-5">
              <p data-testid="dash-seguimientos-hoy" className="text-2xl font-bold text-amber-600">{seguimientosHoy}</p>
              <p className="mt-0.5 text-xs text-amber-700">Seguimientos hoy</p>
            </Card>
          </div>
          <Card className="p-5">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900">Tareas pendientes</h4>
              <button onClick={() => go("tareas")} className="text-xs font-medium text-[#2d5a3d] hover:underline">Ver todas</button>
            </div>
            <div className="mt-3 space-y-2.5">
              {pendientes.length === 0 && <p className="text-xs text-gray-400">Sin tareas pendientes</p>}
              {pendientes.map((t) => (
                <div key={t.id} className="flex items-start gap-2 text-xs">
                  <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${t.prioridad === "Alta" ? "bg-red-500" : t.prioridad === "Media" ? "bg-amber-400" : "bg-emerald-500"}`} />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-gray-800">{t.titulo}</p>
                    <p className="text-gray-400">{leadName(t.leadId)} · {fmtDate(t.fechaLimite)}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="font-semibold text-gray-900">Actividad reciente</h3>
        <div className="mt-4 divide-y divide-gray-50">
          {actividad.length === 0 && <p className="py-3 text-sm text-gray-400">Aún no hay interacciones registradas</p>}
          {actividad.map((a, i) => (
            <div key={i} data-testid={`actividad-${i}`} className="flex items-center gap-4 py-3 text-sm">
              <span className="w-24 shrink-0 text-xs text-gray-400">{fmtDate(a.fecha)}</span>
              <span className="rounded-full bg-[#2d5a3d]/10 px-2.5 py-0.5 text-xs font-medium text-[#2d5a3d]">{a.tipo}</span>
              <span className="font-medium text-gray-800">{a.lead}</span>
              <span className="truncate text-gray-500">{a.resumen}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
